import { Brick, registerBrick, Query, ErrorFlow } from 'olympe';

const cleanseQuery = (query) => {
    const pattern = /':p(\d+)'/g;
    return query.replace(pattern, ":p$1");
}

const removeIfNotInMapper = ({mapper, data, bindings}) => {

    const createBindings = ({ value }) => {
        const newParam = `p${bindings.paramIndex}`;
        const binding = `:${newParam}`;
        bindings.values[newParam] = value;
        bindings.paramIndex++;
        return binding;
    };

    const fieldToColumn = (key) => mapper[key];

    for (const key in data) {
        const column = fieldToColumn(key);
        if (column) {
            const newBinding = createBindings({ value: data[key] });
            data[column] = newBinding;
            if(column!==key) delete data[key];
        } else {
            delete data[key];
        }
    }

    return data;
};

const generateUpdateQuery = ({ tableName, mapper, updateConditions, data }) => {

    let bindings = {
        values: {},
        paramIndex: 0
    }

    const finalConditions = updateConditions.reduce( (acc, curr) => {
        const dbKey = Object.entries(curr)[0][0];
        const dataKey = Object.entries(curr)[0][1];
        acc[dbKey] = data[dataKey];
        return acc;
    },{})

    const cleanedData = removeIfNotInMapper({mapper, data, bindings});

    // Create an array of updates from the data object
    const updates = Object.keys(cleanedData)
        .map((key) => {
            const value = cleanedData[key];
            if (typeof value === "string") {
            // Escape single quotes in the value
                return `${key} = '${value.replace(/'/g, "''")}'`;
            } else if (value === null) {
                return `${key} = NULL`;
            } else {
                return `${key} = ${value}`;
            }
        })
        .join(", ");

    // Create the condition string from the conditions object
    const conditionStrings = Object.keys(finalConditions)
        .map((key) => {
            const value = finalConditions[key];
            if (typeof value === "string") {
            // Escape single quotes in the condition value
            return `${key} = '${value.replace(/'/g, "''")}'`;
            } else if (value === null) {
            return `${key} IS NULL`;
            } else {
            return `${key} = ${value}`;
            }
        })
        .join(" AND ");

    let query = `UPDATE ${tableName} SET ${updates} WHERE ${conditionStrings};`;
    const cleansedQuery = cleanseQuery(query);

    // Construct the UPDATE query
    return {
        query: cleansedQuery,
        bindings: bindings.values
    }
};

const generateInsertQuery = ({ tableName, mapper, data }) => {

    let bindings = {
        values: {},
        paramIndex: 0
    }

    const cleanedData = removeIfNotInMapper({mapper, data, bindings});
    // Extract column names and values from the data object
    const columns = Object.keys(cleanedData);

    const values = Object.values(cleanedData).map((value) => {
        if (typeof value === "string") {
            // Escape single quotes by replacing each ' with ''
            return `'${value.replace(/'/g, "''")}'`;
        } else if (value === null) {
            return "NULL";
        } else {
            return value;
        }
    });

    // Join columns and values into comma-separated strings
    const columnsString = columns.join(", ");
    const valuesString = values.join(", ");

    let query = `INSERT INTO ${tableName} (${columnsString}) VALUES (${valuesString});`;
    const cleansedQuery = cleanseQuery(query);

    // Construct the INSERT query
    return {
        query: cleansedQuery,
        bindings: bindings.values
    }
};

const generateQuery = ({modelTag, updateConditions, payload}) => {
 
    const { toDelete, toInsert, toUpdate } = payload;
    
    const paramConfigs = Query.instancesOf(modelTag, false, "server").executeFromCache();

    const firstParam = paramConfigs.getFirst()
    const tableName = `${firstParam.get('01862c935347456d04b2')}.${firstParam.get('0185c080fff9d4533458')}`

    const mapper = paramConfigs.reduce((acc, curr) => {
            const column = curr.get("0185c081050d996c424c");
            const field = curr.get("0185c081098c285960d8");
            acc[field] = `${column}`
        return acc;
    }, {});

    const toUpdateMerged = [...(Array.isArray(toDelete) ? toDelete : []), ...(Array.isArray(toUpdate) ? toUpdate : [])];
    const insertItemsCount = toInsert?.length || 0; 
    const updateItemsCount = toUpdateMerged?.length || 0;

    const insertQueries = insertItemsCount > 0 ? toInsert
        .map((item) => generateInsertQuery({ tableName, mapper, data: item })) : [];

    const updateQueries = updateItemsCount > 0 ? toUpdateMerged
        .map((item) => generateUpdateQuery({ tableName, mapper, updateConditions, data: item })) : [];

    return [...insertQueries,...updateQueries]
}

export default class BuildTransactionSQLQueryV2 extends Brick {

    /**
     * @override
     * @protected
     * @param {!BrickContext} $
     * @param {ListDef} modelTags
     * @param {*} payload
     * @param {function()} forwardEvent
     * @param {function(string)} setQuery
     * @param {function(*)} setBindings
     * @param {function(*)} setErrorFlow
     */
    update($, [modelTags, payload, updateConditions], [forwardEvent, setQuery, setErrorFlow]) {
      
        try {
         
            const queries = payload.reduce( (acc, curr, index) => {
                    const newQuery = generateQuery({modelTag: modelTags[index], updateConditions:  updateConditions ? updateConditions[index] || [] : [], payload: curr} );
                    return [...acc ,...newQuery]     
                },[]
            )
           
            setQuery(queries);
            forwardEvent();

        } catch (error) {

            setErrorFlow(ErrorFlow.create(`Error ${error} during the execution`));

        }
    }
}

registerBrick('018f7fdb0e078652328b', BuildTransactionSQLQueryV2);
